/**
 * AppBar Component
 *
 * Application navigation bar with hamburger menu and user avatar.
 * Features a full-screen overlay menu when opened.
 *
 * Features:
 * - HamburgerMenu toggle button (left)
 * - UserAvatar with dropdown (right)
 * - Full-screen overlay menu with navigation items
 * - Footer with Brilliant.no link
 * - Keyboard navigation support
 * - Escape key to close menu
 *
 * Ported from Vue AppBar.vue
 *
 * @example
 * ```tsx
 * <AppBar
 *   menuItems={[
 *     { label: 'Schedule', route: '/booking', icon: 'calendar', testId: 'nav-schedule' },
 *     { label: 'Office', route: '/office', icon: 'floor-plan', testId: 'nav-office' },
 *   ]}
 *   userAvatarSrc="/avatar.jpg"
 *   userName="John Doe"
 *   onNavigate={(route) => router.push(route)}
 *   onLogout={() => auth.logout()}
 * />
 * ```
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { HamburgerMenu } from "@/components/ui/navigation/HamburgerMenu";
import { Icon } from "@/components/ui/display/Icon";
import { MenuItem } from "@/components/ui/navigation/MenuItem";
import { UserMenu } from "@/components/ui/navigation/UserMenu";
import { cx } from "@/utils/cx";
import styles from "./AppBar.module.css";
import type { AppBarProps } from "./types";

export const AppBar: React.FC<AppBarProps> = ({
  menuItems = [],
  onNavigate,
  className = "",
  "data-testid": testId,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuCaptureRef = useRef<HTMLDivElement>(null);

  // Toggle main menu
  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  // Handle navigation
  const handleNavigation = useCallback(
    (route: string) => {
      onNavigate?.(route);
      setMenuOpen(false);
    },
    [onNavigate],
  );

  // Prefetch route chunks on hover/focus for faster navigation
  const handlePrefetch = useCallback((route: string) => {
    switch (route) {
      case "/booking":
        import("@/pages/booking/BookingPage");
        break;
      case "/office":
        import("@/pages/office/OfficePage");
        break;
      case "/admin":
        import("@/pages/admin/AdminPage");
        break;
      case "/superadmin":
        import("@/pages/superadmin/SuperadminPage");
        break;
    }
  }, []);

  // Handle Brilliant link
  const openHomeSite = useCallback(() => {
    window.open("https://www.brilliant.no", "_blank", "noopener,noreferrer");
  }, []);

  // Handle Escape key to close menu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  // Prevent body scroll and mark overlay state when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.body.dataset.overlayOpen = "true";
    } else {
      document.body.style.overflow = "";
      delete document.body.dataset.overlayOpen;
    }

    return () => {
      document.body.style.overflow = "";
      delete document.body.dataset.overlayOpen;
    };
  }, [menuOpen]);

  // Ensure focus is not trapped in aria-hidden overlay when closed
  useEffect(() => {
    if (menuOpen) return;
    const active = document.activeElement;
    if (active && menuCaptureRef.current?.contains(active)) {
      menuButtonRef.current?.focus();
    }
  }, [menuOpen]);

  // Build class names
  const containerClasses = cx(styles.appBarContainer, className);
  const menuCaptureClasses = cx(styles.menuCapture, !menuOpen && styles.hide);

  return (
    <header className={containerClasses} data-testid={testId}>
      <HamburgerMenu
        ref={menuButtonRef}
        open={menuOpen}
        onToggle={toggleMenu}
        aria-controls="main-navigation"
      />

      {/* User menu with avatar and dropdown */}
      <UserMenu data-testid="user-menu" />

      {/* Full-screen menu overlay */}
      <div
        ref={menuCaptureRef}
        className={menuCaptureClasses}
        aria-hidden={!menuOpen}
      >
        <nav
          id="main-navigation"
          className={styles.content}
          aria-label="Main"
          role="menu"
        >
          {menuItems.map((item) => (
            <div
              key={item.route}
              onMouseEnter={() => handlePrefetch(item.route)}
              onFocus={() => handlePrefetch(item.route)}
            >
              <MenuItem
                label={item.label}
                icon={item.icon}
                data-testid={item.testId}
                onClick={() => handleNavigation(item.route)}
              />
            </div>
          ))}
        </nav>

        <footer className={styles.footer}>
          <ActionButton
            label="Brilliant"
            icon={<Icon name="copyright" size="sm" aria-hidden={true} />}
            variant="info"
            iconPosition="left"
            onClick={openHomeSite}
          />
        </footer>
      </div>
    </header>
  );
};

AppBar.displayName = "AppBar";
